import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { sendMessage } from "@/lib/rexxponsApi";

export const ContactModal = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) {
      toast.error("Please write a short message first.");
      return;
    }
    setBusy(true);
    try {
      await sendMessage({ name: name.trim(), email: email.trim(), message: text });
      toast.success("Message sent — thank you. We read every note.");
      setName("");
      setEmail("");
      setMessage("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-lg bg-card border border-border rounded-2xl p-7 shadow-2xl"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="contact-close-btn"
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X strokeWidth={1.5} className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/30">
                <Mail strokeWidth={1.5} className="w-4 h-4 text-primary" />
              </span>
              <h2 className="font-heading text-2xl font-medium tracking-tight">Send a message</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              A question, feedback, or an idea for Rexxpons? Drop us a short note.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  data-testid="contact-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
                />
                <input
                  data-testid="contact-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="Email (optional)"
                  className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
              <textarea
                data-testid="contact-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your message…"
                rows={4}
                maxLength={2000}
                className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors resize-none"
              />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm tracking-[0.08em] uppercase rounded-lg px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send strokeWidth={1.8} className="w-4 h-4" />
                {busy ? "Sending…" : "Send message"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
